import { useRegisterSW } from 'virtual:pwa-register/react';
import {
  IconChartBar,
  IconClipboardList,
  IconCloudOff,
  IconDownload,
  IconFileText,
  IconLogout,
  IconMenu2,
  IconRefresh,
  IconSettings,
  IconSignature,
  IconUserCircle,
  IconUsers,
} from '@tabler/icons-react';
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Banner, Dropdown, LanguageSwitch, Logo, Sheet } from '@/components/common';
import type { Language } from '@/i18n';
import { logger } from '@/lib/logger';
import { navigate } from '@/lib/router';
import type { ClinicUser } from '@/types/api';
import type { Doctor } from '@/utils/doctors';

type Props = {
  user: ClinicUser;
  roleLabel: string;
  outbox: { pending: number; failed: number; online: boolean; sync: () => void };
  /** The doctor selector: whom new entries are recorded for. */
  doctors: Doctor[];
  doctorId: string | null;
  onDoctor: (id: string) => void;
  onLogout: () => void;
  onLanguage: (lang: Language) => void;
  /** The middle of the top bar (the calendar filter and the next patient). */
  center?: ReactNode;
  children: ReactNode;
};

type InstallPrompt = Event & { prompt: () => Promise<void> };

const TitleSlot = createContext<HTMLElement | null>(null);

/** Shows its children in the top bar, right of the logo (the open patient's name and back button). */
export function TopbarTitle({ children }: { children: ReactNode }) {
  const slot = useContext(TitleSlot);
  return slot ? createPortal(children, slot) : null;
}

export function AppShell(p: Props) {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [titleSlot, setTitleSlot] = useState<HTMLDivElement | null>(null);
  const [install, setInstall] = useState<InstallPrompt | null>(null);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisterError: (err) => logger.warn('service worker registration failed', err),
  });

  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setInstall(e as InstallPrompt);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  const go = (route: Parameters<typeof navigate>[0]) => {
    setMenuOpen(false);
    navigate(route);
  };

  return (
    <TitleSlot.Provider value={titleSlot}>
      <div className="shell">
        <header className="topbar">
          <div className="topbar__start">
            <button
              type="button"
              className="topbar__brand"
              aria-label="Dentlog"
              onClick={() => navigate({ name: 'patients' })}
            >
              <Logo size={30} />
            </button>
            <div className="topbar__title" ref={setTitleSlot} />
          </div>
          <div className="topbar__center">{p.center}</div>
          <div className="topbar__end">
            {(!p.outbox.online || p.outbox.pending > 0) && (
              <span className="badge badge--offline" title={t('shell.queuedTitle')}>
                <IconCloudOff size={14} />
                {p.outbox.online
                  ? t('shell.syncShort', { count: p.outbox.pending })
                  : t('shell.offline')}
              </span>
            )}
            {/* Hidden for a doctor: they record only for themselves, there is nothing to pick. */}
            {(p.doctors.length > 1 ||
              (p.doctors.length === 1 && p.doctors[0].id !== p.user.id)) && (
              <Dropdown
                variant="bar"
                ariaLabel={t('shell.doctor')}
                value={p.doctorId}
                options={p.doctors.map((d) => ({ value: d.id, label: d.name }))}
                onChange={p.onDoctor}
              />
            )}
            <span className="topbar__clinic">{p.user.clinic.name}</span>
            <button
              type="button"
              className="icon-btn"
              aria-label={t('shell.menu')}
              onClick={() => setMenuOpen(true)}
            >
              <IconMenu2 size={24} />
            </button>
          </div>
        </header>

        <main className="main">
          {needRefresh && (
            <div style={{ marginBottom: 16 }}>
              <Banner
                tone="info"
                action={
                  <button
                    type="button"
                    className="btn btn--sm btn--dark"
                    onClick={() => updateServiceWorker(true)}
                  >
                    {t('shell.update')}
                  </button>
                }
              >
                {t('shell.newVersion')}
              </Banner>
            </div>
          )}
          {p.outbox.failed > 0 && (
            <div style={{ marginBottom: 16 }}>
              <Banner tone="error">{t('shell.failed', { count: p.outbox.failed })}</Banner>
            </div>
          )}
          {p.children}
        </main>

        {menuOpen && (
          <Sheet
            title={
              <>
                {p.user.name} <span className="muted">/ {p.roleLabel}</span>
              </>
            }
            onClose={() => setMenuOpen(false)}
          >
            <div className="stack">
              <nav className="stack" aria-label={t('shell.menu')}>
                <button
                  type="button"
                  className="btn btn--block"
                  onClick={() => go({ name: 'patients' })}
                >
                  <IconUsers size={20} /> {t('shell.patients')}
                </button>
                <button
                  type="button"
                  className="btn btn--block"
                  onClick={() => go({ name: 'offers' })}
                >
                  <IconFileText size={20} /> {t('shell.offers')}
                </button>
                <button
                  type="button"
                  className="btn btn--block"
                  onClick={() => go({ name: 'reports' })}
                >
                  <IconChartBar size={20} /> {t('shell.reports')}
                </button>
                <button
                  type="button"
                  className="btn btn--block"
                  onClick={() => go({ name: 'protocol' })}
                >
                  <IconClipboardList size={20} /> {t('shell.protocol')}
                </button>
                <button
                  type="button"
                  className="btn btn--block"
                  onClick={() => go({ name: 'consents' })}
                >
                  <IconSignature size={20} /> {t('shell.consents')}
                </button>
                <button
                  type="button"
                  className="btn btn--block"
                  onClick={() => go({ name: 'settings' })}
                >
                  <IconSettings size={20} /> {t('shell.settings')}
                </button>
                <button
                  type="button"
                  className="btn btn--block"
                  onClick={() => go({ name: 'profile' })}
                >
                  <IconUserCircle size={20} /> {t('shell.profile')}
                </button>
                {p.outbox.pending > 0 && p.outbox.online && (
                  <button type="button" className="btn btn--block" onClick={p.outbox.sync}>
                    <IconRefresh size={20} /> {t('shell.syncNow', { count: p.outbox.pending })}
                  </button>
                )}
                {install && (
                  <button
                    type="button"
                    className="btn btn--block"
                    onClick={async () => {
                      await install.prompt();
                      setInstall(null);
                    }}
                  >
                    <IconDownload size={20} /> {t('shell.install')}
                  </button>
                )}
                <LanguageSwitch onChange={p.onLanguage} />
                <button type="button" className="btn btn--block btn--primary" onClick={p.onLogout}>
                  <IconLogout size={20} /> {t('shell.logout')}
                </button>
              </nav>
              {p.outbox.pending > 0 && (
                <p className="small muted">
                  {t('shell.logoutWarning', { count: p.outbox.pending })}
                </p>
              )}
            </div>
          </Sheet>
        )}
      </div>
    </TitleSlot.Provider>
  );
}
